// Phase 29.6.2 · Manager view — /manager/team-day-summaries.
// One card per reportee-day. Uses server-side subtree ∩ zone filter.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, User, Phone, Target } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const INR = new Intl.NumberFormat("en-IN",
  { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export default function TeamDaySummaries() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [reportees, setReportees] = useState([]);
  // Deep-link from a "Your day summary" notification: ?date=YYYY-MM-DD.
  const deepLinkDate = searchParams.get("date") || "";
  const [fromDate, setFromDate] = useState(deepLinkDate);
  const [toDate, setToDate] = useState(deepLinkDate);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    api.get("/daily-working-summary/team", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setReportees(r.data.reportees || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate]);

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="tds-root">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          Manager view
        </div>
        <h1 className="text-2xl font-extrabold text-neutral-900">
          Team day summaries
        </h1>
      </div>

      <div className="flex items-center gap-2 flex-wrap"
           data-testid="tds-date-filter">
        <Input type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="tds-from-date"
                placeholder="From" className="h-9 max-w-[180px]" />
        <span className="text-xs text-neutral-400">to</span>
        <Input type="date" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="tds-to-date"
                placeholder="To" className="h-9 max-w-[180px]" />
      </div>

      <div className="text-xs text-neutral-500"
           data-testid="tds-reportee-list">
        Reportees with closed days: <b>{reportees.length}</b>
        {reportees.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {reportees.map((r) => (
              <span key={r.id}
                    className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-[11px] font-mono">
                {r.name || r.phone}
              </span>
            ))}
          </div>
        )}
      </div>

      {loading && !items && (
        <div className="py-8 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {items && items.length === 0 && (
        <Card className="border-dashed border-neutral-300 rounded-2xl"
              data-testid="tds-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            No reportees have closed days in this range.
          </CardContent>
        </Card>
      )}

      {items && items.map((r) => (
        <Card key={r.id}
              className="border-neutral-200 rounded-2xl"
              data-testid="tds-summary-card"
              data-user-id={r.se_user_id}
              data-date={r.date}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-neutral-500" />
                <div>
                  <div className="text-sm font-bold text-neutral-900">
                    {r.se?.name || r.se_user_id.slice(0, 8)}
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono">
                    {r.date} · {r.se?.role_code || "SE"}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-extrabold text-neutral-900">
                  {INR.format(r.secondary_value_inr)}
                </div>
                <div className="text-[11px] text-neutral-500">
                  {r.secondary_kg.toFixed(1)} kg
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center">
              <Metric label="TC" value={r.tc} />
              <Metric label="PC" value={r.pc} tone="emerald" />
              <Metric label="Hrs" value={r.total_market_hours == null ? "—" : `${Math.floor(Math.round(r.total_market_hours * 60) / 60)}h ${String(Math.round(r.total_market_hours * 60) % 60).padStart(2, "0")}m`} />
              <Metric label="Beat cov" value={`${r.beat_coverage_pct}%`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Metric({ label, value, tone }) {
  const cls = tone === "emerald"
    ? "text-emerald-700"
    : "text-neutral-800";
  return (
    <div>
      <div className="text-[10px] text-neutral-400 uppercase">{label}</div>
      <div className={`text-sm font-bold ${cls}`}>{value}</div>
    </div>
  );
}
